import {useEffect, useRef} from 'react'
import {useLocation} from 'react-router-dom'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useClaraPreferences} from './use-clara-preferences'
import {loadOnInteractionOrIdle} from '../utils/load-on-interaction'
import {SITE_COUNTRY_MAP} from '../constants'
import {useBrand} from '../contexts/brand-context'

const normalizeConfigKey = (value = '') => String(value).trim().toLowerCase().replace('_', '-')

export const getClaraConfigForBrandAndLocale = (claraConfig, brandId, localeId) => {
    const normalizedBrand = normalizeConfigKey(brandId)
    const normalizedLocale = normalizeConfigKey(localeId)

    return (
        claraConfig?.[normalizedBrand]?.[normalizedLocale] ||
        claraConfig?.default ||
        null
    )
}

const isClaraDebugEnabled = () => {
    if (typeof window === 'undefined') return false

    try {
        const params = new URLSearchParams(window.location.search)
        return params.get('claraDebug') === '1' || window.localStorage?.getItem('claraDebug') === '1'
    } catch (e) {
        return false
    }
}

const claraDebugLog = (...args) => {
    if (!isClaraDebugEnabled()) return
    console.info('[ClaraChat]', ...args)
}

const setClaraDebugState = (patch) => {
    if (typeof window === 'undefined' || !isClaraDebugEnabled()) return

    const current = window.__CLARA_CHAT_DEBUG__ || {}
    window.__CLARA_CHAT_DEBUG__ = {
        ...current,
        ...patch,
        updatedAt: new Date().toISOString()
    }
}

const useClaraChat = ({enabled = true} = {}) => {
    const {claraConfig, isLoading} = useClaraPreferences()
    const {site, locale} = useMultiSite()
    const {brandId} = useBrand()
    const location = useLocation()
    const isDesktop = useBreakpointValue({base: false, lg: true})

    // `claraConfig` is a brand-new object reference on every render (it is
    // re-parsed from sessionStorage each call). Depending on it directly made
    // the init effect re-run on every render, which repeatedly tore down/re-added
    // listeners and created many MutationObservers, preventing the Salesforce
    // bootstrap from settling (utilAPI never became ready). We instead expose the
    // latest values through a ref and initialize exactly once.
    const latestRef = useRef({})
    latestRef.current = {claraConfig, site, locale, brandId, isDesktop}

    const localeId = locale?.id || locale?.alias || ''
    const selectedConfig = getClaraConfigForBrandAndLocale(claraConfig, brandId, localeId)
    const hasSelectedConfig = Boolean(selectedConfig)
    const isReady = !isLoading && Boolean(brandId) && Boolean(localeId) && hasSelectedConfig
    const initializedRef = useRef(false)

    // Effect to initialize Clara Chat once
    useEffect(() => {
        setClaraDebugState({
            enabled,
            isLoading,
            hasSelectedConfig,
            siteId: site?.id,
            localeId,
            brandId,
            pathname: location?.pathname
        })

        if (!isReady) {
            claraDebugLog('Initialization skipped', {
                reason: isLoading ? 'preferences-loading' : 'missing-brand-locale-config'
            })
            return
        }

        if (initializedRef.current) {
            claraDebugLog('Initialization already completed; skipping re-init')
            return
        }
        initializedRef.current = true

        const {
            claraConfig: latestConfig,
            site: latestSite,
            locale: latestLocale,
            brandId: latestBrandId
        } = latestRef.current
        const latestLocaleId = latestLocale?.id || latestLocale?.alias || ''
        const config = getClaraConfigForBrandAndLocale(
            latestConfig,
            latestBrandId,
            latestLocaleId
        )
        const SCRIPT_ID = 'clara-bootstrap-script'

        const isFrench = normalizeConfigKey(latestLocaleId).startsWith('fr')

        const brand = normalizeConfigKey(latestBrandId)
        const userCountry = SITE_COUNTRY_MAP[latestSite?.id] || 'US'
        const userLanguage = isFrench ? 'French' : 'English'
        const chatMedium = 'Web Chat'

        // This observer is used for accessibility. If the iframe which is loaded async is not visible add aria-hidden true
        const observeIframes = () => {
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeName === 'IFRAME') {
                            if (node.id === 'embeddedMessagingSiteContextFrame') {
                                const style = window.getComputedStyle(node)
                                if (style?.display === 'none') {
                                    node.setAttribute('aria-hidden', 'true')
                                }
                            }
                        }
                    })
                })
            })

            observer.observe(document.body, {
                childList: true,
                subtree: true
            })

            return observer
        }

        const onEmbeddedMessagingReady = () => {
            try {
                window.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields({
                    f_Language: userLanguage,
                    Chat_Medium: chatMedium,
                    f_BrandName: brand,
                    f_Country: userCountry
                })
                claraDebugLog('Prechat hidden fields set', {
                    f_Language: userLanguage,
                    Chat_Medium: chatMedium,
                    f_BrandName: brand,
                    f_Country: userCountry
                })
            } catch (err) {
                console.error('Error setting hidden prechat fields:', err)
                claraDebugLog('Prechat hidden fields failed', {error: err?.message})
            }
        }

        const onEmbeddedMessagingWindowMaximized = () => {
            // Apply z-index to chat elements
            const element = document.getElementById('embeddedMessagingFrame')
            if (element) {
                element.style.zIndex = '9999'
            }
        }

        const onEmbeddedMessagingWindowMinimized = () => {
            // Apply position styles when chat is minimized
            const element = document.getElementById('embeddedMessagingFrame')
            if (element) {
                // Check if we're on a PDP (Product Detail Page)
                const isPDP = window.location.pathname.match(/\/p\//)
                element.style.bottom = isPDP ? '118px' : '12px'
                element.style.right = latestRef.current.isDesktop ? '24px' : '16px'
            }
        }

        // When debugging, surface the underlying Salesforce/runtime errors that the
        // bootstrap script may throw (these are otherwise easy to miss). Use the
        // capture phase so failed resource loads (script/iframe/CORS) are caught too.
        const onGlobalError = (event) => {
            const target = event?.target
            if (target && target !== window && (target.src || target.href)) {
                claraDebugLog('resource load failed during chat init', {
                    tag: target.tagName,
                    url: target.src || target.href
                })
                return
            }
            claraDebugLog('window error during chat init', {
                message: event?.message,
                source: event?.filename,
                line: event?.lineno
            })
        }
        const onUnhandledRejection = (event) => {
            claraDebugLog('unhandled rejection during chat init', {
                reason: event?.reason?.message || String(event?.reason)
            })
        }
        if (isClaraDebugEnabled()) {
            window.addEventListener('error', onGlobalError, true)
            window.addEventListener('unhandledrejection', onUnhandledRejection)
        }

        const initEmbeddedMessaging = () => {
            try {
                const bootstrap = window.embeddedservice_bootstrap

                if (!bootstrap) {
                    claraDebugLog('embeddedservice_bootstrap missing during init')
                    setClaraDebugState({initBlocked: 'missing-bootstrap'})
                    return
                }

                claraDebugLog('Initializing embedded messaging', {
                    organizationId: config.OrganizationId,
                    developerName: config.DeveloperName,
                    initUrl: config.InitUrl,
                    scriptUrl: config.ScriptUrl,
                    localeLanguage: isFrench ? 'fr' : 'en'
                })

                window.addEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReady)
                window.addEventListener(
                    'onEmbeddedMessagingWindowMaximized',
                    onEmbeddedMessagingWindowMaximized
                )
                window.addEventListener(
                    'onEmbeddedMessagingWindowMinimized',
                    onEmbeddedMessagingWindowMinimized
                )

                bootstrap.settings.language = isFrench ? 'fr' : 'en'
                bootstrap.init(config.OrganizationId, config.DeveloperName, config.InitUrl, {
                    scrt2URL: config.Url
                })
                setClaraDebugState({
                    initCalled: true,
                    developerName: config.DeveloperName,
                    scriptUrl: config.ScriptUrl
                })
            } catch (err) {
                console.error('Error initializing Clara Chat:', err)
                claraDebugLog('Init failed', {error: err?.message})
                setClaraDebugState({initError: err?.message})
            }
        }

        const loadScript = () => {
            if (document.getElementById(SCRIPT_ID)) {
                claraDebugLog('Bootstrap script already present')
                return
            }

            const script = document.createElement('script')
            script.id = SCRIPT_ID
            script.src = config.ScriptUrl
            script.onload = initEmbeddedMessaging
            script.onerror = () => {
                console.error('Failed to load Clara bootstrap script')
                claraDebugLog('Bootstrap script load failed', {src: script.src})
                setClaraDebugState({scriptLoadError: script.src})
            }

            claraDebugLog('Appending bootstrap script', {src: script.src})
            setClaraDebugState({scriptAppended: script.src})

            document.head.appendChild(script)
        }

        const iframeObserver = observeIframes()

        // Defer Clara Chat loading until user interaction or idle
        // This improves TTI by not blocking the main thread on initial load
        const interactionCleanup = loadOnInteractionOrIdle(() => {
            if (document.readyState === 'loading') {
                claraDebugLog('Waiting for DOMContentLoaded to load script')
                document.addEventListener('DOMContentLoaded', loadScript)
            } else {
                claraDebugLog('Loading script immediately')
                loadScript()
            }
        }, { timeout: 3000 })

        return () => {
            interactionCleanup?.()
            document.removeEventListener('DOMContentLoaded', loadScript)
            window.removeEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReady)
            window.removeEventListener(
                'onEmbeddedMessagingWindowMaximized',
                onEmbeddedMessagingWindowMaximized
            )
            window.removeEventListener(
                'onEmbeddedMessagingWindowMinimized',
                onEmbeddedMessagingWindowMinimized
            )
            window.removeEventListener('error', onGlobalError, true)
            window.removeEventListener('unhandledrejection', onUnhandledRejection)
            iframeObserver?.disconnect()
        }
    }, [isReady])

    // Separate effect to control visibility based on enabled prop
    useEffect(() => {
        if (isLoading) return

        let hasAppliedVisibility = false

        const applyChatVisibility = () => {
            try {
                const bootstrap = window.embeddedservice_bootstrap

                if (!bootstrap?.utilAPI) {
                    claraDebugLog('Visibility skipped: utilAPI not ready')
                    setClaraDebugState({utilApiReady: false})
                    return false
                }

                setClaraDebugState({utilApiReady: true, visibilityEnabled: enabled})

                if (enabled) {
                    // Show chat button when enabled
                    bootstrap.utilAPI.showChatButton()
                    claraDebugLog('showChatButton called')
                } else {
                    // Hide chat button when disabled (e.g., on checkout page)
                    bootstrap.utilAPI.hideChatButton()
                    claraDebugLog('hideChatButton called')
                }

                hasAppliedVisibility = true
                return true
            } catch (err) {
                console.error('Error toggling Clara Chat visibility:', err)
                claraDebugLog('Visibility toggle failed', {error: err?.message})
                return false
            }
        }

        const onEmbeddedMessagingReady = () => {
            applyChatVisibility()
        }

        // Try once shortly after render, and again when embedded messaging reports ready.
        const timer = setTimeout(() => {
            applyChatVisibility()
        }, 100)

        // Some environments do not consistently emit onEmbeddedMessagingReady.
        // Poll utilAPI readiness briefly and apply visibility once it becomes available.
        let retries = 0
        const maxRetries = 50
        const pollTimer = setInterval(() => {
            if (hasAppliedVisibility) {
                clearInterval(pollTimer)
                return
            }

            retries += 1
            const applied = applyChatVisibility()
            if (applied || retries >= maxRetries) {
                if (!applied) {
                    claraDebugLog('Visibility polling timed out before utilAPI became ready')
                    setClaraDebugState({visibilityPollTimedOut: true})
                }
                clearInterval(pollTimer)
            }
        }, 300)

        window.addEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReady)

        return () => {
            clearTimeout(timer)
            clearInterval(pollTimer)
            window.removeEventListener('onEmbeddedMessagingReady', onEmbeddedMessagingReady)
        }
    }, [enabled, isLoading])

    // Effect to update chat position when route changes or breakpoint changes
    useEffect(() => {
        const updateChatPosition = () => {
            const element = document.getElementById('embeddedMessagingFrame')
            // Only apply position styles if the chat is minimized
            if (element && element.classList.contains('isMinimized')) {
                const isPDP = location.pathname.match(/\/p\//)
                element.style.bottom = isPDP ? '118px' : '12px'
                element.style.right = isDesktop ? '24px' : '16px'
            }
        }

        // Update immediately
        updateChatPosition()

        // Also update after a small delay to ensure the element exists
        const timer = setTimeout(updateChatPosition, 100)

        return () => clearTimeout(timer)
    }, [location.pathname, isDesktop])

    return {
        isReady
    }
}

/**
 * Programmatically opens Clara Chat by clicking the embedded messaging button.
 * Reuses the same approach as checkout-header.jsx.
 */
export const openClaraChat = (e) => {
    e?.preventDefault()
    const chatButton = document.getElementById('embeddedMessagingConversationButton')
    if (chatButton) {
        chatButton.click()
    }
}

export {useClaraChat}
